/* utils.js */

"use strict";

const _ = require("lodash");
const papaparse = require("papaparse");

const templateFileName = "site-template.csv";
const csvDefaultOptions = {
    header: true,
    delimiter: ",",
    skipEmptyLines: true,
};

/**
 * @typedef {Object} fieldDefinition
 * @property {string} title - field column title
 * @property {string} path - field path in device
 * @property {string} type - field dataType
 * @property {boolean} [required] - Indicates if field is required
 * @property {object} [typeInfo] - custom field type info
 * @property {boolean} [isCustomField] - indicates if field is custom field
 */

/**
 * @type {fieldDefinition[]}
 */
const baseFieldsData = [
    {
        title: "Type",
        path: "locationType",
        type: "string",
    },
    {
        title: "Code Name",
        path: "codeName",
        type: "string",
        required: true,
    },
    {
        title: "Name",
        path: "name",
        type: "string",
        required: true,
    },
    {
        title: "Description",
        path: "description",
        type: "string",
        required: false,
    },
    {
        title: "Parent Site Name",
        path: "parentName",
        type: "string",
        required: false,
    },
    {
        title: "Credential",
        path: "credsName",
        type: "string",
        required: false,
    },
    {
        title: "Admin Contact",
        required: false,
        path: "adminContact",
        type: "array",
    },
    {
        title: "Technical Contact",
        required: false,
        path: "technicalContact",
        type: "array",
    },
    {
        title: "Location",
        required: false,
        path: "location",
        type: "string",
    },
    {
        title: "Discovery Level",
        path: "discoveryLevel",
        required: false,
        type: "string",
        excludeFromTemplate: true,
    },
    {
        title: "Address",
        required: false,
        path: "address",
        type: "string",
    },
    {
        title: "Country/Region",
        path: "country",
        type: "string",
        required: false,
    },
    {
        title: "Latitude",
        path: "latitude",
        type: "string",
        required: false,
    },
    {
        title: "Longitude",
        path: "longitude",
        type: "string",
        required: false,
    },
    {
        title: "Timezone",
        path: "timezone",
        type: "string",
        required: false,
    },
    {
        title: "Enable auto-assignment of Devices to this Site",
        path: "autoAssignEnabled",
        type: "boolean",
        required: false,
    },
    {
        title: "Auto Assign Device Property",
        path: "autoAssignProperty",
        type: "string",
        required: false,
    },
    {
        title: "Auto-assign Device Hostname RegEx",
        path: "autoAssignRegex",
        type: "string",
        required: false,
    },
];

/**
 * format custom fields data
 * @param {Array<{label:string, value:string, [systemOwned]:boolean, [enabled]:boolean, [typeInfo]:Object}>} fields
 * @return {fieldDefinition[]}
 */

const requiredFieldName = (title) => `** ${title} **`;

const getFieldsLabels = (fieldsData) =>
    fieldsData.map(({title, required}) => (required ? requiredFieldName(title) : title));

const generateCSV = (data, options) => {
    const csvOptions = _.defaults(options, csvDefaultOptions);
    const fields = _.get(csvOptions, "columns", []);

    return papaparse.unparse({fields, data});
};

const parseCSV = async (data, options) => {
    const csvOptions = _.defaults(options, csvDefaultOptions);
    const resp = await papaparse.parse(data, csvOptions, (err, data) =>
        err ? Promise.reject(err) : Promise.resolve(data)
    );
    if (resp.data && resp.data[0]) {
        let respArray = [Object.keys(resp.data[0])];
        resp.data.forEach(function (item) {
            respArray.push(Object.values(item));
        });
        return Promise.resolve(respArray);
    }
    return Promise.reject(resp.errors[0] || "unknown");
};

/**
 * match headers with fields definitions
 * @param {string[]} headers
 * @param {fieldDefinition[]} fieldsDefinitions
 * @return {fieldDefinition[]}
 */
const getListOfHeadersDefinitions = (fieldsDefinitions, headers) =>
    headers.map((headerName) =>
        fieldsDefinitions.find(
            ({title, required}) =>
                headerName === title ||
                (required && headerName === requiredFieldName(title)) ||
                headerName === _.startCase(title)
        )
    );

module.exports = {
    templateFileName,
    baseFieldsData,
    getFieldsLabels,
    generateCSV,
    parseCSV,
    getListOfHeadersDefinitions,
};
